'use client';

import { useTheme, themeContext } from '@/lib/utils/themeContext';
import Container from './Container';
import classNames from 'classnames';
import ShapeElement, { Shape } from './Shape';
import { ThemeColor, themeColors } from '@/lib/config/colors';
import { ContentWidthsProvider } from '@/lib/utils/contentSizeContext';

type SectionProps = {
  id?: string;
  size?: 'small' | 'medium' | 'full';
  color?: ThemeColor;
  backgroundColor?: 'white' | 'sand';
  shape?: Shape;
  shapePosition?: 'left' | 'right';
  className?: string;
  children: React.ReactNode;
};

const getSectionWidths = (size: 'small' | 'medium' | 'full') => {
  switch (size) {
    case 'small':
      return {
        sm: 456,
        md: 552,
        lg: 496,
        xl: 624,
        xxl: 752,
      };
    case 'medium':
      return {
        sm: 456,
        md: 552,
        lg: 744,
        xl: 936,
        xxl: 1128,
      };
    case 'full':
      return {
        sm: 608,
        md: 736,
        lg: 992,
        xl: 1248,
        xxl: 1504,
      };
  }
};

const Section = ({
  id,
  size = 'full',
  color,
  backgroundColor,
  shape,
  shapePosition = 'left',
  className,
  children,
}: SectionProps) => {
  const theme = useTheme();
  const widths = getSectionWidths(size);

  return (
    <ContentWidthsProvider {...widths}>
      <themeContext.Provider
        value={{
          color: color || theme.color,
          backgroundColor: backgroundColor || theme.backgroundColor,
        }}
      >
        <section
          id={id}
          className={classNames(
            'relative scroll-mt-72',
            {
              'bg-white': backgroundColor === 'white',
              'bg-sand-100': backgroundColor === 'sand',
              'py-20': backgroundColor === 'sand' && !shape,
              'pb-20': backgroundColor === 'sand' && shape,
              'pt-24': shape === 'blob' || shape === 'bow' || shape === 'wave' || shape === 'grid',
              'pt-32': shape === 'circle' || shape === 'plus',
            },
            className
          )}
        >
          {shape && (
            <div className="container absolute left-1/2 top-0 -translate-x-1/2">
              <div
                className={classNames('absolute top-0 z-10 flex w-[24rem] -translate-y-1/2 justify-center', {
                  '-left-48':
                    shapePosition === 'left' &&
                    (shape === 'blob' || shape === 'bow' || shape === 'wave' || shape === 'circle'),
                  '-right-48':
                    shapePosition === 'right' &&
                    (shape === 'blob' || shape === 'bow' || shape === 'wave' || shape === 'circle'),
                  '-left-56': shapePosition === 'left' && shape === 'plus',
                  '-right-56': shapePosition === 'right' && shape === 'plus',
                  '-left-72': shapePosition === 'left' && shape === 'grid',
                  '-right-72': shapePosition === 'right' && shape === 'grid',
                })}
              >
                <ShapeElement
                  className={classNames(themeColors[color || theme.color][400].fill, {
                    'w-[24rem]': shape === 'plus' || shape === 'wave',
                    'w-[16rem]': shape === 'blob' || shape === 'bow' || shape === 'circle' || shape === 'grid',
                  })}
                  shape={shape}
                />
              </div>
            </div>
          )}
          <Container
            className={classNames('relative', {
              'grid grid-cols-12': size === 'small' || size === 'medium',
            })}
          >
            <div
              className={classNames('relative z-20 col-start-1 col-end-13', {
                'sm:col-start-2 sm:col-end-11 lg:col-start-4 lg:col-end-10': size === 'small',
                'sm:col-start-2 sm:col-end-11': size === 'medium',
              })}
            >
              {children}
            </div>
          </Container>
        </section>
      </themeContext.Provider>
    </ContentWidthsProvider>
  );
};

export default Section;
